import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import {
  ShoppingCart, Trash2, MapPin, Shield, ChevronRight, ChevronLeft,
  Package, Tag, Truck, RotateCcw, Search, Heart, User, Menu, ChevronDown, X, UserCircle, Home
} from "lucide-react";
import axios from "axios";
import { getCart, removeFromCart, updateLineQty, getCartCount } from "@/utils/cart";
import SiteFooter from "@/components/SiteFooter";
import { lineIsBackorder, useDispatchTexts } from "@/utils/stockDispatch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Electronics", "Fashion", "Home & Living", "Sports", "Beauty", "Toys & Kids", "Automotive", "Books", "Grocery", "Health"];

// ── Mobile Cart Item ──────────────────────────────────────────────────────────
function MobileCartItem({ item, deliveryDate, onRemove, onQtyChange, navigate, dispatchTexts }) {
  const discPct = item.mrp && item.mrp > item.price
    ? Math.round(((item.mrp - item.price) / item.mrp) * 100) : 0;

  return (
    <div className="bg-white mt-2 px-4 py-4" data-testid={`cart-item-${item.id}`}>
      <div className="flex gap-3">
        {/* Product image */}
        <Link to={`/products/${item.slug}`} className="flex-shrink-0 w-24 h-28 bg-slate-50 border border-slate-100 rounded overflow-hidden flex items-center justify-center">
          {item.image
            ? <img src={item.image} alt={item.name} className="w-full h-full object-contain p-2" loading="lazy" decoding="async" onError={e => { e.target.style.display = "none"; }} />
            : <Package className="w-8 h-8 text-slate-300" />}
        </Link>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <Link to={`/products/${item.slug}`} className="text-sm font-medium text-slate-900 line-clamp-2 leading-snug hover:text-orange-600 block">
            {item.name}
          </Link>

          {/* Variant tags */}
          {item.variantLines?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {item.variantLines.map(l => (
                <span key={l.key} className="text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                  {[l.color, l.size].filter(Boolean).join(" / ")} × {l.qty}
                </span>
              ))}
            </div>
          )}

          {/* Rating */}
          {item.rating > 0 && (
            <div className="flex items-center gap-1 mt-1">
              <div className="flex items-center gap-0.5 bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                {item.rating.toFixed(1)} ★
              </div>
              {item.numReviews > 0 && <span className="text-[11px] text-slate-400">{item.numReviews.toLocaleString()}+</span>}
            </div>
          )}

          {/* Price row */}
          <div className="flex items-baseline gap-1.5 mt-1.5">
            {discPct > 0 && <span className="text-xs font-bold text-green-600">↓{discPct}%</span>}
            {item.mrp > item.price && <span className="text-xs text-slate-400 line-through">₹{item.mrp.toLocaleString()}</span>}
            <span className="text-base font-bold text-slate-900">₹{item.price.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Qty selector + delivery */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
        {/* Qty for first/only variant */}
        {item.variantLines.map((l, idx) => idx === 0 ? (
          <div key={l.key} className="flex items-center border border-slate-300 rounded-full overflow-hidden">
            <button
              onClick={() => onQtyChange(item.id, l.key, l.qty - 1)}
              className="w-9 h-9 flex items-center justify-center text-slate-600 hover:bg-slate-100 text-lg font-bold transition-colors"
              data-testid={`cart-qty-minus-${item.id}-${l.key}`}
            >−</button>
            <span className="w-8 text-center text-sm font-bold text-slate-800 select-none" data-testid={`cart-qty-${item.id}-${l.key}`}>{l.qty}</span>
            <button
              onClick={() => onQtyChange(item.id, l.key, l.qty + 1)}
              className="w-9 h-9 flex items-center justify-center text-slate-600 hover:bg-slate-100 text-lg font-bold transition-colors"
              data-testid={`cart-qty-plus-${item.id}-${l.key}`}
            >+</button>
          </div>
        ) : null)}

        {/* Delivery date */}
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <Truck className="w-3.5 h-3.5 flex-shrink-0" />
          <span>By <span className="font-semibold text-slate-700">{deliveryDate}</span></span>
        </div>
      </div>

      {/* Dispatch info for multi-line variants */}
      {item.stockMode && (
        <div className="mt-2 space-y-0.5">
          {item.variantLines.map(l => {
            const oos = lineIsBackorder(item, l);
            return (
              <p key={l.key} className={`text-[11px] flex items-center gap-1 ${oos ? "text-amber-600" : "text-green-600"}`}>
                <Truck className="w-3 h-3" /> {[l.color, l.size].filter(Boolean).join(" / ") || "Item"}: {oos ? dispatchTexts.outOfStock : dispatchTexts.inStock}
              </p>
            );
          })}
        </div>
      )}

      {/* Action buttons — 3 equal outlined */}
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100">
        <button
          onClick={() => onRemove(item.id)}
          className="py-2 text-xs text-slate-700 font-semibold border border-slate-300 rounded-full hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
          data-testid={`cart-remove-${item.id}`}
        >Remove</button>
        <button className="py-2 text-xs text-slate-700 font-semibold border border-slate-300 rounded-full hover:bg-slate-50 transition-colors">
          Wishlist
        </button>
        <button
          onClick={() => navigate(`/products/${item.slug}`)}
          className="py-2 text-xs text-slate-700 font-semibold border border-slate-300 rounded-full hover:bg-orange-50 hover:text-orange-600 hover:border-orange-300 transition-colors"
        >Buy Now</button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CartPage() {
  const [cart, setCart] = useState(getCart());
  const [cartCount, setCartCount] = useState(getCartCount());
  const [showDeliveryDrawer, setShowDeliveryDrawer] = useState(false);
  const [cartUi, setCartUi] = useState({ delivery_days: 3, show_savings_zone: true, cart_cta_text: "Proceed to buy" });
  const navigate = useNavigate();
  const dispatchTexts = useDispatchTexts();

  const refresh = () => { setCart(getCart()); setCartCount(getCartCount()); };

  useEffect(() => {
    window.addEventListener("it_cart_updated", refresh);
    return () => window.removeEventListener("it_cart_updated", refresh);
  }, []);

  useEffect(() => {
    axios.get(`${API}/checkout-settings`).then(res => {
      if (res.data?.cart_ui) setCartUi(res.data.cart_ui);
    }).catch(() => {});
  }, []);

  const handleRemove = (id) => { removeFromCart(id); refresh(); };

  const handleQtyChange = (productId, lineKey, newQty) => {
    const it = cart.find(i => i.id === productId);
    const ln = it?.variantLines?.find(l => l.key === lineKey);
    if (it?.stockMode === "strict" && typeof ln?.stock === "number" && newQty > ln.stock) newQty = ln.stock;
    updateLineQty(productId, lineKey, newQty);
    refresh();
  };

  const subtotal  = cart.reduce((sum, item) => sum + item.price * item.totalQty, 0);
  const mrpTotal  = cart.reduce((sum, item) => sum + (item.mrp || item.price) * item.totalQty, 0);
  const discount  = Math.max(0, mrpTotal - subtotal);
  const totalQty  = cart.reduce((s, i) => s + i.totalQty, 0);

  const deliveryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + (cartUi.delivery_days || 3));
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  })();

  // ── Shared drawer ────────────────────────────────────────────────────────────
  const DeliveryDrawer = () => (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={() => setShowDeliveryDrawer(false)} />
      <div className="w-full sm:w-[400px] bg-white flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-bold text-slate-900">Select delivery address</h2>
          <button onClick={() => setShowDeliveryDrawer(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500" data-testid="close-delivery-drawer">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 my-3 border-t-2 border-dashed border-slate-200" />
        <div className="px-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Saved addresses</p>
          <button onClick={() => { setShowDeliveryDrawer(false); navigate("/customer-login"); }} className="w-full flex items-center gap-3 px-4 py-4 border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all group" data-testid="login-to-see-addresses-btn">
            <div className="w-10 h-10 rounded-full bg-slate-100 group-hover:bg-blue-100 flex items-center justify-center flex-shrink-0 transition-colors">
              <UserCircle className="w-6 h-6 text-slate-400 group-hover:text-blue-500 transition-colors" />
            </div>
            <span className="text-sm brand-text font-semibold group-hover:underline">Login to see your saved addresses</span>
            <ChevronRight className="w-4 h-4 text-slate-400 ml-auto flex-shrink-0" />
          </button>
          <div className="mt-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Or enter a pincode</p>
            <div className="flex gap-2">
              <input type="text" maxLength={6} placeholder="Enter 6-digit pincode" className="flex-1 px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200" data-testid="pincode-input" />
              <button className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors">Apply</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* ═══════════════════════════ MOBILE LAYOUT ═══════════════════════════ */}
      <div className="block md:hidden bg-[#f1f3f6] min-h-screen">
        {/* Header */}
        <header className="bg-white sticky top-0 z-40 shadow-sm">
          <div className="flex items-center px-4 py-3 gap-3">
            <button onClick={() => navigate(-1)} className="p-1 -ml-1 rounded-full hover:bg-slate-100 transition-colors" aria-label="Back">
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <span className="text-base font-bold text-slate-900 flex-1">
              My Cart {totalQty > 0 && <span className="text-slate-500 font-normal">({totalQty})</span>}
            </span>
            <Link to="/" className="text-sm font-black tracking-tight">
              <BrandLogo size="sm" />
            </Link>
          </div>
        </header>

        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 px-8 text-center">
            <ShoppingCart className="w-16 h-16 text-slate-200 mb-4" />
            <h2 className="text-lg font-bold text-slate-800 mb-2">Your cart is empty!</h2>
            <p className="text-sm text-slate-500 mb-6">Add items to it now.</p>
            <Link to="/" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3 rounded-full text-sm transition-colors">
              Shop Now
            </Link>
          </div>
        ) : (
          <>
            {/* Address row */}
            <div className="bg-white px-4 py-3 flex items-center gap-2 border-b border-slate-200 cursor-pointer active:bg-slate-50" onClick={() => setShowDeliveryDrawer(true)}>
              <Home className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <span className="text-sm text-slate-700 flex-1">
                Deliver to <strong>India</strong>&nbsp;
                <span className="text-slate-400 text-xs">· Enter pincode</span>
              </span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>

            {/* Cart items */}
            {cart.map(item => (
              <MobileCartItem
                key={item.id}
                item={item}
                deliveryDate={deliveryDate}
                onRemove={handleRemove}
                onQtyChange={handleQtyChange}
                navigate={navigate}
                dispatchTexts={dispatchTexts}
              />
            ))}

            {/* Savings zone */}
            {cartUi.show_savings_zone && (
              <div className="mt-2 bg-white border-t border-slate-200">
                <div className="px-4 py-3 flex items-center gap-2.5 border-b border-slate-100">
                  <span className="bg-slate-800 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-wider">WOW!</span>
                  <span className="text-sm font-bold text-slate-800">Savings zone</span>
                </div>
                <button
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors active:bg-slate-100"
                  onClick={() => navigate("/checkout")}
                >
                  <span className="text-sm text-slate-600">View all coupons and offers</span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              </div>
            )}

            {/* Bottom spacer for sticky bar */}
            <div className="h-32" />
          </>
        )}

        {/* Sticky bottom bar */}
        {cart.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-[0_-4px_24px_rgba(0,0,0,0.12)]" data-testid="mobile-cart-bottom-bar">
            {discount > 0 && (
              <div className="bg-[#e8f5e9] px-4 py-1.5 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-[#2e7d32] flex-shrink-0" />
                <span className="text-xs font-semibold text-[#2e7d32]">
                  You'll save ₹{discount.toLocaleString()} on this order
                </span>
              </div>
            )}
            <div className="flex items-center justify-between px-4 py-3 gap-3">
              <div className="min-w-0">
                {mrpTotal > subtotal && (
                  <p className="text-xs text-slate-400 line-through leading-none">₹{mrpTotal.toLocaleString()}</p>
                )}
                <p className="text-xl font-black text-slate-900 leading-tight">₹{subtotal.toLocaleString()}</p>
              </div>
              <button
                onClick={() => navigate("/checkout")}
                data-testid="mobile-cart-proceed-btn"
                className="bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bold px-7 py-3 rounded-full text-sm flex-shrink-0 shadow-md transition-colors"
              >
                {cartUi.cart_cta_text || "Proceed to buy"} →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════ DESKTOP LAYOUT ══════════════════════════ */}
      <div className="hidden md:block min-h-screen bg-[#f1f3f6]">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex-shrink-0">
                <BrandLogo size="md" />
              </Link>
              <div className="flex-1 max-w-2xl">
                <div className="flex">
                  <input type="text" placeholder="Search for products, brands and more..." className="flex-1 px-4 py-2.5 border border-r-0 border-slate-300 rounded-l-lg text-sm focus:outline-none focus:border-orange-500" />
                  <button className="bg-orange-500 hover:bg-orange-600 text-white px-4 rounded-r-lg transition-colors"><Search className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button className="flex flex-col items-center text-slate-600 hover:text-orange-500 transition-colors"><User className="w-5 h-5" /><span className="text-xs mt-0.5">Account</span></button>
                <button className="flex flex-col items-center text-slate-600 hover:text-orange-500 transition-colors"><Heart className="w-5 h-5" /><span className="text-xs mt-0.5">Wishlist</span></button>
                <button className="flex flex-col items-center text-orange-500 transition-colors relative">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="text-xs mt-0.5 font-semibold">Cart</span>
                  {cartCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{cartCount}</span>}
                </button>
              </div>
            </div>
          </div>
          <div className="bg-slate-800">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center overflow-x-auto no-scrollbar">
                <button className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2.5 text-sm font-medium flex-shrink-0"><Menu className="w-4 h-4" />All Categories<ChevronDown className="w-3 h-3" /></button>
                {CATEGORIES.map(cat => <button key={cat} className="text-slate-300 hover:text-white px-3 py-2.5 text-sm whitespace-nowrap hover:bg-slate-700 transition-colors flex-shrink-0">{cat}</button>)}
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 py-4">
          {cart.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-12 flex flex-col items-center justify-center text-center">
              <ShoppingCart className="w-20 h-20 text-slate-200 mb-4" />
              <h2 className="text-xl font-bold text-slate-800 mb-2">Your cart is empty!</h2>
              <p className="text-slate-500 mb-6">Add items to it now.</p>
              <Link to="/" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-sm transition-colors">Shop Now</Link>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-4 items-start">
              {/* ── Left column ── */}
              <div className="flex-1 min-w-0 space-y-3">
                <div className="bg-white rounded-sm shadow-sm px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    <span className="text-sm text-slate-700">Deliver to <span className="font-semibold">India</span></span>
                  </div>
                  <button onClick={() => setShowDeliveryDrawer(true)} className="text-sm brand-text border border-current px-3 py-1 rounded-sm hover:opacity-80 transition-colors font-medium flex-shrink-0" data-testid="enter-delivery-pincode-btn">Enter Delivery Pincode</button>
                </div>
                <div className="bg-white rounded-sm shadow-sm px-4 py-3 flex items-center justify-between">
                  <div>
                    <h1 className="text-lg font-semibold text-slate-900">My Cart</h1>
                    <p className="text-xs text-slate-500">{totalQty} items</p>
                  </div>
                  <span className="text-sm text-slate-500 hidden sm:block">PRICE</span>
                </div>

                {cart.map((item) => {
                  const itemTotal = item.price * item.totalQty;
                  const saving = item.mrp ? (item.mrp - item.price) * item.totalQty : 0;
                  const discPct = item.mrp && item.mrp > item.price ? Math.round(((item.mrp - item.price) / item.mrp) * 100) : 0;
                  return (
                    <div key={item.id} className="bg-white rounded-sm shadow-sm px-4 py-4" data-testid={`cart-item-${item.id}`}>
                      <div className="flex gap-4">
                        <Link to={`/products/${item.slug}`} className="flex-shrink-0 w-24 h-28 bg-slate-50 rounded overflow-hidden border border-slate-100 flex items-center justify-center">
                          {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" onError={e => { e.target.style.display = "none"; }} /> : <Package className="w-10 h-10 text-slate-300" />}
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={`/products/${item.slug}`} className="text-sm font-medium text-slate-800 hover:text-blue-600 line-clamp-2 leading-snug mb-1 block">{item.name}</Link>
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {item.variantLines.map(l => (
                              <span key={l.key} className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{[l.color, l.size].filter(Boolean).join(" / ")} × {l.qty}</span>
                            ))}
                          </div>
                          {item.stockMode && (
                            <div className="space-y-0.5 mb-1.5" data-testid="cart-dispatch">
                              {item.variantLines.map(l => {
                                const oos = lineIsBackorder(item, l);
                                return (
                                  <p key={l.key} className={`text-[11px] flex items-center gap-1 ${oos ? "text-amber-600" : "text-green-600"}`}>
                                    <Truck className="w-3 h-3" /> {[l.color, l.size].filter(Boolean).join(" / ") || "Item"}: {oos ? dispatchTexts.outOfStock : dispatchTexts.inStock}
                                  </p>
                                );
                              })}
                            </div>
                          )}
                          {item.rating > 0 && (
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <div className="flex items-center gap-0.5 bg-green-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">{item.rating.toFixed(1)} <span className="text-[10px]">★</span></div>
                              {item.numReviews > 0 && <span className="text-xs text-slate-400">({item.numReviews.toLocaleString()})</span>}
                            </div>
                          )}
                          <p className="text-xs text-slate-500 flex items-center gap-1 mb-2"><Truck className="w-3 h-3" /> Delivery by <span className="font-semibold text-slate-700">{deliveryDate}</span></p>
                          <div className="flex items-baseline gap-1.5 mb-3 sm:hidden">
                            {discPct > 0 && <span className="text-sm font-bold text-green-600">-{discPct}%</span>}
                            <span className="text-base font-bold text-slate-900">₹{itemTotal.toLocaleString()}</span>
                            {item.mrp > item.price && <span className="text-xs text-slate-400 line-through">₹{(item.mrp * item.totalQty).toLocaleString()}</span>}
                          </div>
                          <div className="space-y-2 mb-3">
                            {item.variantLines.map(l => (
                              <div key={l.key} className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 w-20 truncate">{[l.color, l.size].filter(Boolean).join(" / ") || "Qty"}:</span>
                                <div className="flex items-center border border-slate-300 rounded overflow-hidden">
                                  <button onClick={() => handleQtyChange(item.id, l.key, l.qty - 1)} className="px-2.5 py-1 text-slate-600 hover:bg-slate-100 text-sm font-bold transition-colors" data-testid={`cart-qty-minus-${item.id}-${l.key}`}>−</button>
                                  <span className="px-3 py-1 text-sm font-semibold text-slate-800 border-x border-slate-300 min-w-[2rem] text-center" data-testid={`cart-qty-${item.id}-${l.key}`}>{l.qty}</span>
                                  <button onClick={() => handleQtyChange(item.id, l.key, l.qty + 1)} className="px-2.5 py-1 text-slate-600 hover:bg-slate-100 text-sm font-bold transition-colors" data-testid={`cart-qty-plus-${item.id}-${l.key}`}>+</button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center gap-0 border-t border-slate-100 pt-2">
                            <button onClick={() => handleRemove(item.id)} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-red-600 px-3 py-1.5 hover:bg-slate-50 rounded transition-colors" data-testid={`cart-remove-${item.id}`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                            <div className="w-px h-4 bg-slate-200 mx-1" />
                            <button className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-blue-600 px-3 py-1.5 hover:bg-slate-50 rounded transition-colors"><Heart className="w-3.5 h-3.5" /> Save for later</button>
                            <div className="w-px h-4 bg-slate-200 mx-1" />
                            <button onClick={() => navigate(`/products/${item.slug}`)} className="flex items-center gap-1.5 text-xs brand-text hover:opacity-80 px-3 py-1.5 rounded font-medium transition-colors">Buy this now</button>
                          </div>
                        </div>
                        <div className="hidden sm:flex flex-col items-end flex-shrink-0 min-w-[90px]">
                          <span className="text-lg font-bold text-slate-900">₹{itemTotal.toLocaleString()}</span>
                          {item.mrp > item.price && <><span className="text-xs text-slate-400 line-through">₹{(item.mrp * item.totalQty).toLocaleString()}</span>{discPct > 0 && <span className="text-xs text-green-600 font-semibold">{discPct}% off</span>}</>}
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="bg-white rounded-sm shadow-sm px-4 py-4 lg:hidden">
                  <button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-sm text-base transition-colors" data-testid="place-order-btn-mobile" onClick={() => navigate("/checkout")}>
                    {cartUi.cart_cta_text || "Proceed to Checkout"}
                  </button>
                </div>
              </div>

              {/* ── Right column ── */}
              <div className="lg:w-80 flex-shrink-0 space-y-3 lg:sticky lg:top-24 lg:self-start">
                {discount > 0 && <div className="bg-[#388e3c] text-white text-sm font-semibold text-center py-2 rounded-sm">Your net savings: ₹{discount.toLocaleString()}</div>}
                <div className="bg-white rounded-sm shadow-sm px-4 py-4">
                  <h3 className="text-base font-semibold text-slate-500 uppercase tracking-wide pb-3 border-b border-slate-200 mb-3">Price Details</h3>
                  <div className="space-y-2.5 mb-3">
                    <div className="flex justify-between text-sm"><span className="text-slate-700">Price ({totalQty} items)</span><span className="text-slate-900 font-medium">₹{mrpTotal.toLocaleString()}</span></div>
                    {discount > 0 && <div className="flex justify-between text-sm"><span className="text-slate-700">Discount</span><span className="text-green-600 font-medium">− ₹{discount.toLocaleString()}</span></div>}
                    <div className="flex justify-between text-sm"><span className="text-slate-700">Delivery Charges</span><span className="text-slate-500 font-medium">Calculated at checkout</span></div>
                  </div>
                  <div className="border-t border-dashed border-slate-300 pt-3 mb-3">
                    <div className="flex justify-between"><span className="text-base font-bold text-slate-900">Total Amount</span><span className="text-base font-bold text-slate-900">₹{subtotal.toLocaleString()}</span></div>
                  </div>
                  {discount > 0 && <div className="bg-green-50 text-green-700 text-sm font-semibold text-center py-2 rounded mb-3">You will save ₹{discount.toLocaleString()} on this order</div>}
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-4"><Shield className="w-3.5 h-3.5 flex-shrink-0" /><span>Safe and Secure Payments. Easy returns. 100% Authentic products.</span></div>
                  <button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-sm text-base transition-colors" data-testid="place-order-btn" onClick={() => navigate("/checkout")}>
                    {cartUi.cart_cta_text || "Proceed to Checkout"}
                  </button>
                </div>
                <div className="bg-white rounded-sm shadow-sm px-4 py-3">
                  <div className="grid grid-cols-3 gap-3">
                    {[{ icon: Truck, label: "Doorstep Delivery" }, { icon: RotateCcw, label: "Easy Returns" }, { icon: Tag, label: "Best Prices" }].map(({ icon: Icon, label }) => (
                      <div key={label} className="flex flex-col items-center gap-1 text-center">
                        <Icon className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
                        <span className="text-[10px] text-slate-500 leading-tight">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <SiteFooter />
      </div>

      {/* Shared delivery drawer */}
      {showDeliveryDrawer && <DeliveryDrawer />}
    </>
  );
}
